import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  getPushNotificationDashboard,
  getPushNotificationLogs,
} from '../../services/admin_services';

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'company', label: 'Company' },
  { value: 'advisor', label: 'Advisor' },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: 'paid', label: 'Paid' },
  { value: 'free-trial', label: 'Free Trial' },
  { value: 'freemium', label: 'Freemium' },
];

const BILLING_CYCLE_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
  { value: 'quarterly', label: 'Quarterly' },
];

const initialFilters = {
  accountId: '',
  accountType: '',
  paymentType: '',
  billingCycle: '',
  registrationDateFilter: 'all',
  registrationStartDate: '',
  registrationEndDate: '',
};

function buildVariables(values) {
  return {
    accountId: values.accountId?.trim() || undefined,
    accountType: values.accountType || undefined,
    paymentType: values.paymentType || undefined,
    billingCycle: values.billingCycle || undefined,
    registrationDateFilter: values.registrationDateFilter || undefined,
    registrationStartDate: values.registrationStartDate || undefined,
    registrationEndDate: values.registrationEndDate || undefined,
  };
}

function normalizeToArray(value) {
  if (!value) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  return [value].filter(Boolean);
}

function buildDashboardVariables(values) {
  return {
    paymentType: normalizeToArray(values.paymentType),
    billingCycle: normalizeToArray(values.billingCycle),
    registrationDateFilter: values.registrationDateFilter || undefined,
    registrationStartDate: values.registrationStartDate || undefined,
    registrationEndDate: values.registrationEndDate || undefined,
  };
}

function calculatePercentage(part, total) {
  if (!total || Number(total) === 0) {
    return 0;
  }
  const percentage = (Number(part || 0) / Number(total)) * 100;
  if (!Number.isFinite(percentage)) {
    return 0;
  }
  return Math.min(100, Math.max(0, Number(percentage.toFixed(1))));
}

function formatInteger(value) {
  if (value === null || value === undefined) {
    return '—';
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toLocaleString() : String(value);
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function normalizeList(value) {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value.filter(Boolean) : [value].filter(Boolean);
}

function truncate(text, maxLength = 60) {
  if (!text) {
    return '—';
  }

  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

function prettyPrintJSON(payload) {
  if (!payload) {
    return '—';
  }

  if (typeof payload === 'string') {
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch (_error) {
      return payload;
    }
  }

  try {
    return JSON.stringify(payload, null, 2);
  } catch (_error) {
    return '—';
  }
}

function parseRequestPayload(request) {
  if (!request) {
    return {};
  }

  if (typeof request === 'object') {
    return request;
  }

  try {
    return JSON.parse(request);
  } catch (_error) {
    return {};
  }
}

function PushNotificationLogs() {
  const [filters, setFilters] = useState(initialFilters);
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const selectedRequest = parseRequestPayload(selectedLog?.request);
  const advisorCoverage = calculatePercentage(
    dashboardData?.advisorDeviceAccounts,
    dashboardData?.totalAdvisors
  );
  const companyCoverage = calculatePercentage(
    dashboardData?.companyDeviceAccounts,
    dashboardData?.totalCompanies
  );

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'registrationDateFilter' && value === 'all'
        ? { registrationStartDate: '', registrationEndDate: '' }
        : {}),
    }));
  };

  const fetchLogs = async (customFilters = filters) => {
    setLoading(true);
    setError('');
    setHasLoaded(false);
    setLogs([]);

    try {
      const response = await getPushNotificationLogs(
        buildVariables(customFilters)
      );
      const items = response?.data?.getPushNotificationLogs || [];
      setLogs(items);
      setHasLoaded(true);
    } catch (fetchError) {
      console.error('Error fetching push notification logs', fetchError);
      setError('Could not load logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async (customFilters = filters) => {
    setDashboardLoading(true);
    setDashboardError('');

    try {
      const response = await getPushNotificationDashboard(
        buildDashboardVariables(customFilters)
      );
      setDashboardData(response?.data?.getPushNotificationDashboard || null);
    } catch (fetchError) {
      console.error('Error fetching push notification dashboard', fetchError);
      setDashboardError('Could not load dashboard. Please try again.');
    } finally {
      setDashboardLoading(false);
    }
  };

  const handleSearch = (event) => {
    event.preventDefault();
    fetchDashboard(filters);
    fetchLogs(filters);
  };

  const handleClear = () => {
    const resetFilters = { ...initialFilters };
    setFilters(resetFilters);
    setLogs([]);
    setError('');
    setHasLoaded(false);
    fetchDashboard(resetFilters);
    fetchLogs(resetFilters);
  };

  useEffect(() => {
    fetchDashboard(initialFilters);
    fetchLogs(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ width: '100%', py: 4, px: { xs: 2, md: 8 } }}>
      <Helmet>
        <title>Push Notification Logs</title>
      </Helmet>

      <Typography variant="h4" gutterBottom>
        Push Notification Logs
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Review notification deliveries and results.
      </Typography>

      <Paper
        elevation={0}
        sx={{ p: 3, mt: 2, border: '1px solid #e0e0e0', minHeight: '240px' }}
      >
        <form onSubmit={handleSearch}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Account Type"
                name="accountType"
                value={filters.accountType}
                onChange={handleFilterChange}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {ACCOUNT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Payment Type"
                name="paymentType"
                value={filters.paymentType}
                onChange={handleFilterChange}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {PAYMENT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Billing Cycle"
                name="billingCycle"
                value={filters.billingCycle}
                onChange={handleFilterChange}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {BILLING_CYCLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Account ID"
                name="accountId"
                placeholder="Enter account id"
                value={filters.accountId}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Registration date</FormLabel>
                <RadioGroup
                  row
                  name="registrationDateFilter"
                  value={filters.registrationDateFilter}
                  onChange={handleFilterChange}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label="All time"
                  />
                  <FormControlLabel
                    value="range"
                    control={<Radio />}
                    label="Registration date range"
                  />
                </RadioGroup>
              </FormControl>
              {filters.registrationDateFilter === 'range' ? (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start date"
                      name="registrationStartDate"
                      value={filters.registrationStartDate}
                      onChange={handleFilterChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End date"
                      name="registrationEndDate"
                      value={filters.registrationEndDate}
                      onChange={handleFilterChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              ) : null}
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleClear}
                disabled={loading || dashboardLoading}
              >
                Clear filters
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading || dashboardLoading}
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      <Paper elevation={0} sx={{ p: 3, mt: 2, border: '1px solid #e0e0e0' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontSize: '18px' }}>
              Delivery coverage
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Based on the selected filters.
            </Typography>
          </Box>
          {dashboardLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                Refreshing...
              </Typography>
            </Box>
          ) : null}
        </Box>

        {dashboardError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {dashboardError}
          </Alert>
        ) : null}

        <Grid container spacing={2} sx={{ mt: dashboardError ? 2 : 3 }}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #e0e0e0',
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                // height: '100%',
              }}
            >
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={88}
                  thickness={4}
                  sx={{
                    color: '#e0e0e0',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                  }}
                />
                <CircularProgress
                  variant="determinate"
                  value={advisorCoverage}
                  size={88}
                  thickness={4}
                  color="primary"
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontSize: '20px' }}
                    component="div"
                  >
                    {advisorCoverage}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Advisors
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle1">Advisor devices</Typography>
                <Typography variant="body2" color="text.secondary">
                  Devices with tokens vs total advisors
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {formatInteger(dashboardData?.advisorDeviceAccounts)} /{' '}
                  {formatInteger(dashboardData?.totalAdvisors)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #e0e0e0',
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                // height: '100%',
              }}
            >
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={88}
                  thickness={4}
                  sx={{
                    color: '#e0e0e0',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                  }}
                />
                <CircularProgress
                  variant="determinate"
                  value={companyCoverage}
                  size={88}
                  thickness={4}
                  color="success"
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontSize: '20px' }}
                    component="div"
                  >
                    {companyCoverage}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Companies
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle1">Company devices</Typography>
                <Typography variant="body2" color="text.secondary">
                  Devices with tokens vs total companies
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {formatInteger(dashboardData?.companyDeviceAccounts)} /{' '}
                  {formatInteger(dashboardData?.totalCompanies)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{ mt: 3, border: '1px solid #e0e0e0', overflow: 'hidden' }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading logs...
            </Typography>
          </Box>
        ) : logs.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account Type</TableCell>
                  <TableCell>Payment Type</TableCell>
                  <TableCell>Billing Cycle</TableCell>
                  <TableCell>Attempted</TableCell>
                  <TableCell>Sent</TableCell>
                  <TableCell>Failed</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => {
                  const requestPayload = parseRequestPayload(log?.request);
                  const accountType = requestPayload.accountType || '—';
                  const paymentType = normalizeList(
                    requestPayload.paymentType
                  ).join(', ');
                  const billingCycle = normalizeList(
                    requestPayload.billingCycle
                  ).join(', ');

                  return (
                    <TableRow key={log.id || log.createdAt}>
                      <TableCell>{formatDateTime(log.createdAt)}</TableCell>
                      <TableCell>{accountType || '—'}</TableCell>
                      <TableCell>{paymentType || '—'}</TableCell>
                      <TableCell>{billingCycle || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.attemptedCount ?? 0}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.successCount ?? 0}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.failureCount ?? 0}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Tooltip
                          title={log.message || ''}
                          placement="top-start"
                        >
                          <Typography noWrap>
                            {truncate(log.message, 80)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setSelectedLog(log)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : hasLoaded ? (
          <Box
            sx={{
              py: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No results for the selected filters.
            </Typography>
          </Box>
        ) : null}
      </Paper>

      <Dialog
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Log detail</DialogTitle>
        <DialogContent dividers>
          {selectedLog ? (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(selectedLog.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Account type
                  </Typography>
                  <Typography variant="body1">
                    {selectedRequest?.accountType || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Payment type
                  </Typography>
                  <Typography variant="body1">
                    {normalizeList(selectedRequest?.paymentType).join(', ') ||
                      '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Billing cycle
                  </Typography>
                  <Typography variant="body1">
                    {normalizeList(selectedRequest?.billingCycle).join(', ') ||
                      '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary">
                    Attempted
                  </Typography>
                  <Chip
                    label={selectedLog.attemptedCount ?? 0}
                    color="info"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary">
                    Delivered
                  </Typography>
                  <Chip
                    label={selectedLog.successCount ?? 0}
                    color="success"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary">
                    Failed
                  </Typography>
                  <Chip
                    label={selectedLog.failureCount ?? 0}
                    color="error"
                    size="small"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Message
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedLog.message || '—'}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                Failed endpoints
              </Typography>
              {selectedLog.failedEndpoints &&
              selectedLog.failedEndpoints.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    maxHeight: 160,
                    overflow: 'auto',
                    mb: 2,
                  }}
                >
                  {selectedLog.failedEndpoints.map((endpoint, index) => (
                    <Chip
                      key={`${endpoint}-${index}`}
                      label={endpoint || 'N/A'}
                      color="error"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  No failed endpoints were recorded.
                </Typography>
              )}

              <Typography variant="subtitle2" gutterBottom>
                Request
              </Typography>
              <Box
                component="pre"
                sx={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  p: 2,
                  fontSize: 12,
                  maxHeight: 320,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {prettyPrintJSON(selectedRequest)}
              </Box>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PushNotificationLogs;
